import React from "react"
import types from "prop-types"
import clsx from "clsx"
import Rails from "@rails/ujs"
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd"
import { FormsyHiddenInput, FormsyTextInput, TextInput } from "shared/inputs"
import { Button, CircularIconButton } from "shared/buttons"
import { DragGripHandleIcon } from "shared/icons"
import FormsyValidation from "shared/FormsyValidation"
import FieldLabel from "shared/FieldLabel"
import ItemRow from "shared/ItemRow"
import { squish } from "utils/stringHelpers"
import ImportAnswerOptionsListFromCSVButton from "./ImportAnswerOptionsListFromCSVButton"

const buildNameValidations = ({ listNames }) => ({
  isNotBlankString: true,
  noExcelMetacharacters: true,
  noHTML: true,
  mayNotBeIncludedIn: {
    denyList: listNames,
    canonize: (value) => value?.trim().toLowerCase(),
    errorMessage: "Cannot repeat list names",
  },
})

const buildOptionValidations = ({ options, index }) => ({
  isNotBlankString: true,
  noExcelMetacharacters: true,
  noHTML: true,
  noRepeat: {
    canonize: (value) => squish(value)?.toLowerCase(),
    otherValues: options
      .filter((_option, otherIndex) => otherIndex !== index)
      .map(({ value }) => value),
    errorMessage: "Cannot repeat options in list",
  },
})

const AnswerOptionsListFormFields = ({
  addOption,
  className = "",
  getOptionInputRef,
  initialName = "",
  listNames,
  moveOption,
  nameInputRef = undefined,
  onImportOptions,
  options,
  removeOption,
  updateOption,
}) => (
  <div className={clsx(className)}>
    <FormsyHiddenInput name="authenticity_token" value={Rails.csrfToken()} />
    <FieldLabel className="w-full !max-w-full" boldLabel required text="Name">
      <FormsyTextInput
        ariaLabel="name"
        className="bordered-base-input text-sm bg-white base-input mb-4"
        inputRef={nameInputRef}
        name="answer_options_list[name]"
        validations={buildNameValidations({ listNames })}
        value={initialName}
      />
    </FieldLabel>
    <FieldLabel className="w-full !max-w-full" boldLabel required text="Options">
      <DragDropContext onDragEnd={moveOption}>
        <Droppable droppableId="answer-options-list">
          {
            (dropProvided) => (
              <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                {
                  options.map((option, index) => (
                    <Draggable
                      key={option.id}
                      draggableId={option.id}
                      index={index}
                    >
                      {
                        (dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                          >
                            <ItemRow
                              itemRowHeaderContentClassName="gap-2 flex-row items-center"
                              itemRowHeaderContent={(
                                <>
                                  <DragGripHandleIcon dragProvided={dragProvided} />
                                  <TextInput
                                    uuid={option.id}
                                    ariaLabel={`option ${index + 1}`}
                                    className="h-4 text-sm bg-white base-input"
                                    name="answer_options_list[options][]"
                                    onChange={updateOption(index)}
                                    ref={getOptionInputRef(index)}
                                    value={option.value}
                                  />
                                  <FormsyValidation
                                    name={`options[${index}]`}
                                    validations={buildOptionValidations({ options, index })}
                                    value={option.value}
                                  />
                                </>
                              )}
                              itemRowHeaderControls={(
                                <CircularIconButton
                                  ariaLabel={`remove option ${index + 1}`}
                                  icon="close"
                                  onClick={removeOption(index)}
                                />
                              )}
                            />
                          </div>
                        )
                      }
                    </Draggable>
                  ))
                }
                { dropProvided.placeholder }
              </div>
            )
          }
        </Droppable>
      </DragDropContext>
    </FieldLabel>
    <div className="flex gap-2 items-center">
      <Button className="tertiary-button text-focus" onClick={addOption} text="+ Add Option" ariaLabel="add option" />
      or
      <ImportAnswerOptionsListFromCSVButton onImport={onImportOptions} />
    </div>
  </div>
)

AnswerOptionsListFormFields.propTypes = {
  addOption: types.func.isRequired,
  className: types.string,
  getOptionInputRef: types.func.isRequired,
  initialName: types.string,
  listNames: types.arrayOf(types.string).isRequired,
  moveOption: types.func.isRequired,
  nameInputRef: types.oneOfType([types.func, types.object]),
  onImportOptions: types.func.isRequired,
  options: types.arrayOf(types.shape({
    id: types.string.isRequired,
    value: types.string.isRequired,
  })).isRequired,
  removeOption: types.func.isRequired,
  updateOption: types.func.isRequired,
}

export default AnswerOptionsListFormFields
